import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, ClearIcon, Flex, Input } from '@modusign/design-system';
import { iconNeutralBase, iconNeutralSlightlyDark } from '@modusign/design-system/colors';
import { format, getDefaultOptions } from 'date-fns';
import { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Day, DayPicker, DayPickerSingleProps } from 'react-day-picker';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle } from 'react-use';

import { DefaultOption } from '@app/libs/dateFns';

interface SingleDatePickerProps extends Omit<DayPickerSingleProps, 'mode'> {
  value?: Date;
  footer?: ReactNode;
  field: FieldProps;
}

interface FieldProps {
  placeholder: string;
  style?: CSSProperties;
  isInvalid?: boolean;
  disabled?: boolean;
  onClearDate?: () => void;
}

export default function SingleDatePicker({
  footer,
  value,
  field: { placeholder, style, isInvalid = false, disabled = false, onClearDate },
  ...props
}: SingleDatePickerProps) {
  const [isOpen, toggle] = useToggle(false);
  const { locale }: DefaultOption = getDefaultOptions();

  return (
    <Box height="100%" position="relative" width="100%">
      <OutsideClickHandler onOutsideClick={() => toggle(false)}>
        <Box
          height="100%"
          position="relative"
          sx={{
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          width="100%"
          onClick={() => {
            if (!disabled) {
              toggle();
            }
          }}
        >
          <Input
            backgroundColor={
              disabled ? 'formBackgroundNeutralSlightlyDark' : 'formBackgroundNeutralBase'
            }
            color={disabled ? 'textNeutralVeryLight' : 'textNeutralBase'}
            disabled={disabled}
            isInvalid={isInvalid}
            placeholder={placeholder}
            readOnly
            style={style}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'buttonBorderNeutralSlightlyLight',
                backgroundColor: 'buttonBackgroundNeutralBase',
              },
            }}
            value={value ? format(value, 'yyyy-MM-dd') : ''}
          />
          {onClearDate && value && (
            <Flex
              alignItems="center"
              height="36px"
              justifyContent="center"
              position="absolute"
              right={7}
              textAlign="center"
              top={0}
              width="36px"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                onClearDate();
              }}
            >
              <ClearIcon />
            </Flex>
          )}
          <Flex
            alignItems="center"
            height="36px"
            justifyContent="center"
            position="absolute"
            right={0}
            textAlign="center"
            top={0}
            width="36px"
          >
            <FontAwesomeIcon
              color={disabled ? iconNeutralBase : iconNeutralSlightlyDark}
              height="14px"
              icon={faCalendarDay}
              width="13px"
            />
          </Flex>
        </Box>
        {isOpen && (
          <Card
            display="flex"
            flexDirection="column"
            left={0}
            p={4}
            position="absolute"
            top="42px"
            zIndex={1001}
          >
            <DayPicker
              components={{
                Day: (props) => (
                  <Box
                    color="black"
                    fontSize={2}
                    sx={{
                      '.rdp-button:hover:not([disabled]):not(.rdp-day_selected)': {
                        backgroundColor: 'iconNeutralVeryLight',
                      },
                      '.rdp-day_today:not(.rdp-day_selected)': {
                        border: '1px solid',
                        borderColor: 'buttonBorderNeutralSlightlyLight',
                        backgroundColor: 'white',
                      },
                      '.rdp-day_selected': {
                        backgroundColor: 'formBackgroundEmphasisDark',
                        color: 'white',
                        fontWeight: 500,
                      },
                    }}
                  >
                    <Day {...props} />
                  </Box>
                ),
              }}
              locale={locale}
              mode="single"
              selected={value}
              onDayClick={toggle}
              {...props}
            />
            {footer}
          </Card>
        )}
      </OutsideClickHandler>
    </Box>
  );
}
